import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, Package, Plus, Minus, ClipboardList } from 'lucide-react'
import { useEquipmentStore } from '../stores/equipmentStore'
import { useSupabaseEquipmentStore } from '../stores/supabaseEquipmentStore'
import { useAuthStore } from '../stores/authStore'
import OrdersPage from './OrdersPage'

export default function EquipmentPage() {
  const navigate = useNavigate()
  const { cart, addToCart } = useEquipmentStore()
  const { 
    equipment, 
    loading, 
    error, 
    fetchEquipment, 
    fetchCategories,
    getEquipmentByCategory, 
    getAllCategories 
  } = useSupabaseEquipmentStore()
  const { isAuthenticated } = useAuthStore()

  const [activeTab, setActiveTab] = useState<'equipment' | 'orders'>('equipment')
  const [selectedCategory, setSelectedCategory] = useState<string>('Alle')
  const [selectedSizes, setSelectedSizes] = useState<Record<string, string>>({})
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [addingToCart, setAddingToCart] = useState<string | null>(null)

  useEffect(() => {
    if (isAuthenticated()) {
      fetchEquipment()
      fetchCategories()
    }
  }, [isAuthenticated])

  // Frequent category loading to ensure they're always fresh
  useEffect(() => {
    if (isAuthenticated()) {
      const categoryInterval = setInterval(() => {
        console.log('🔄 Refreshing categories...')
        fetchCategories()
      }, 30000) // Refresh every 30 seconds

      return () => clearInterval(categoryInterval)
    }
  }, [isAuthenticated, fetchCategories])

  // Additional category refresh when switching to equipment tab
  useEffect(() => {
    if (activeTab === 'equipment' && isAuthenticated()) {
      console.log('🔄 Tab switched to equipment - refreshing categories...')
      fetchCategories()
    }
  }, [activeTab, isAuthenticated, fetchCategories])

  const categories = ['Alle', ...getAllCategories()]
  
  const filteredEquipment = selectedCategory === 'Alle' 
    ? equipment 
    : getEquipmentByCategory(selectedCategory)

  const handleAddToCart = (item: any) => {
    const size = selectedSizes[item.id]
    const quantity = quantities[item.id] || 1
    
    // Validate required size selection
    if (item.sizes && item.sizes.length > 0 && !size) {
      alert('Bitte wähle eine Größe aus')
      return
    }

    // Start cart animation
    setAddingToCart(item.id)

    addToCart({
      equipmentId: item.id,
      name: item.name,
      quantity,
      size,
      price: item.price,
    })

    // Reset form
    setSelectedSizes(prev => ({ ...prev, [item.id]: '' }))
    setQuantities(prev => ({ ...prev, [item.id]: 1 }))

    // End cart animation after a short delay
    setTimeout(() => {
      setAddingToCart(null)
    }, 1500)
  }

  const updateQuantity = (itemId: string, change: number) => {
    setQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, (prev[itemId] || 1) + change)
    }))
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #1B7E3A',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }} />
          <style>
            {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
          </style>
          <p style={{
            color: '#6B7280',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Equipment wird geladen...
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          border: '1px solid #fca5a5',
          color: '#dc2626',
          padding: '16px',
          borderRadius: '12px',
          fontWeight: '500'
        }}>
          Fehler beim Laden des Equipments: {error}
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px',
            letterSpacing: '-0.025em'
          }}>
            Equipment & Bestellungen
          </h1>
          <p style={{
            color: '#6b7280',
            fontSize: '18px',
            fontWeight: '500'
          }}>
            Bestelle Vereinsausrüstung und verwalte deine Bestellungen
          </p>
        </div>
        {activeTab === 'equipment' && (
          <button 
            onClick={() => navigate('/cart')}
            style={{
              background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              position: 'relative',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 6px -1px rgba(27, 126, 58, 0.3)',
              alignSelf: 'flex-start'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 8px 12px -1px rgba(27, 126, 58, 0.4)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(27, 126, 58, 0.3)'
            }}
          >
            <ShoppingCart size={16} />
            Zum Warenkorb ({cart.length})
            {cart.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#ef4444',
                color: 'white',
                fontSize: '12px',
                borderRadius: '50%',
                height: '20px',
                width: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700'
              }}>
                {cart.length}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        padding: '24px',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative gradient overlay */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '4px',
          background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
          borderRadius: '16px 16px 0 0'
        }} />
        
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('equipment')}
            style={{
              background: activeTab === 'equipment' 
                ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                : 'transparent',
              color: activeTab === 'equipment' ? 'white' : '#374151',
              border: activeTab === 'equipment' ? 'none' : '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: activeTab === 'equipment' 
                ? '0 2px 4px rgba(27, 126, 58, 0.3)' 
                : 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'equipment') {
                e.currentTarget.style.borderColor = '#1B7E3A'
                e.currentTarget.style.backgroundColor = '#f0fdf4'
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'equipment') {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <Package size={16} />
            Equipment
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            style={{
              background: activeTab === 'orders' 
                ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                : 'transparent',
              color: activeTab === 'orders' ? 'white' : '#374151',
              border: activeTab === 'orders' ? 'none' : '1px solid #d1d5db',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
              boxShadow: activeTab === 'orders' 
                ? '0 2px 4px rgba(27, 126, 58, 0.3)' 
                : 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'orders') {
                e.currentTarget.style.borderColor = '#1B7E3A'
                e.currentTarget.style.backgroundColor = '#f0fdf4'
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'orders') {
                e.currentTarget.style.borderColor = '#d1d5db'
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <ClipboardList size={16} />
            Meine Bestellungen
          </button>
        </div>
      </div>

      {/* Equipment Tab */}
      {activeTab === 'equipment' && (
        <div>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            padding: '24px',
            marginBottom: '24px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              height: '4px',
              background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
              borderRadius: '16px 16px 0 0'
            }} />
            
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#111827',
              marginBottom: '16px',
              letterSpacing: '-0.025em'
            }}>
              Kategorien
            </h3>
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    background: selectedCategory === category 
                      ? 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)' 
                      : 'transparent',
                    color: selectedCategory === category ? 'white' : '#374151',
                    border: selectedCategory === category ? 'none' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedCategory === category 
                      ? '0 2px 4px rgba(27, 126, 58, 0.3)' 
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category) {
                      e.currentTarget.style.borderColor = '#1B7E3A'
                      e.currentTarget.style.backgroundColor = '#f0fdf4'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category) {
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {filteredEquipment.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '64px 20px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '16px',
              border: '1px solid #e5e7eb'
            }}>
              <Package size={64} style={{ 
                margin: '0 auto 16px', 
                color: '#d1d5db' 
              }} />
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#111827',
                marginBottom: '8px'
              }}>
                Keine Equipment-Artikel
              </h3>
              <p style={{
                color: '#6B7280',
                fontSize: '16px',
                fontWeight: '500'
              }}>
                Keine Artikel in dieser Kategorie verfügbar.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {filteredEquipment.map(item => (
                <div key={item.id} style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  borderRadius: '16px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #e5e7eb',
                  padding: '24px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)'
                  e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}
                >
                  {/* Decorative gradient overlay */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    right: '0',
                    height: '4px',
                    background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
                    borderRadius: '16px 16px 0 0'
                  }} />
                  
                  {item.image && (
                    <div style={{
                      width: '100%',
                      height: '192px',
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      borderRadius: '12px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Package size={48} style={{ color: '#9ca3af' }} />
                    </div>
                  )}
                  
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '8px',
                    letterSpacing: '-0.025em'
                  }}>
                    {item.name}
                  </h3>
                  <p style={{
                    color: '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '16px',
                    lineHeight: '1.5'
                  }}>
                    {item.description}
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {item.sizes && item.sizes.length > 0 && (
                        <div>
                          <label style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '4px'
                          }}>
                            Größe
                          </label>
                          <select
                            value={selectedSizes[item.id] || ''}
                            onChange={(e) => setSelectedSizes(prev => ({ ...prev, [item.id]: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '500',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              transition: 'border-color 0.2s ease'
                            }}
                            onFocus={(e) => e.currentTarget.style.borderColor = '#1B7E3A'}
                            onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
                          >
                            <option value="">Größe wählen</option>
                            {item.sizes.map(size => (
                              <option key={size} value={size}>{size}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '4px'
                        }}>
                          Anzahl
                        </label>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <button
                            onClick={() => updateQuantity(item.id, -1)}
                            style={{
                              background: 'transparent',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              padding: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#1B7E3A'
                              e.currentTarget.style.backgroundColor = '#f0fdf4'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db'
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }}
                          >
                            <Minus size={16} />
                          </button>
                          <span style={{
                            padding: '8px 16px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            textAlign: 'center',
                            minWidth: '48px',
                            fontWeight: '600',
                            backgroundColor: 'white'
                          }}>
                            {quantities[item.id] || 1}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            style={{
                              background: 'transparent',
                              border: '1px solid #d1d5db',
                              borderRadius: '8px',
                              padding: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = '#1B7E3A'
                              e.currentTarget.style.backgroundColor = '#f0fdf4'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#d1d5db'
                              e.currentTarget.style.backgroundColor = 'transparent'
                            }}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>

                      <button
                        onClick={() => handleAddToCart(item)}
                        style={{
                          background: addingToCart === item.id 
                            ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' 
                            : 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '12px 16px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: addingToCart === item.id ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          width: '100%',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 2px 4px rgba(27, 126, 58, 0.3)',
                          transform: addingToCart === item.id ? 'scale(1.05)' : 'scale(1)'
                        }}
                        disabled={addingToCart === item.id}
                        onMouseEnter={(e) => {
                          if (addingToCart !== item.id) {
                            e.currentTarget.style.transform = 'translateY(-1px)'
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(27, 126, 58, 0.4)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (addingToCart !== item.id) {
                            e.currentTarget.style.transform = 'translateY(0)'
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(27, 126, 58, 0.3)'
                          }
                        }}
                      >
                        {addingToCart === item.id ? (
                          <>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid #ffffff',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }} />
                            Wird hinzugefügt...
                          </>
                        ) : (
                          <>
                            <ShoppingCart size={16} />
                            In den Warenkorb
                          </>
                        )}
                      </button>
                    </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          padding: '24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient overlay */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            height: '4px',
            background: 'linear-gradient(90deg, #1B7E3A 0%, #22C55E 50%, #1B7E3A 100%)',
            borderRadius: '16px 16px 0 0'
          }} />
          
          <OrdersPage />
        </div>
      )}
      
      {/* CSS Animation Styles */}
      <style>
        {`
          @keyframes spin { 
            0% { transform: rotate(0deg); } 
            100% { transform: rotate(360deg); } 
          }
          @keyframes cartPulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </div>
  )
}
